import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useRef, useState } from 'react';
import 'dayjs/locale/en-au';

import { OVERLAY_Z_INDEX, POPOVER_Z_INDEX } from './inlineEditLayers';
import { formatLocalDateTime } from '../../../../../utils/common';

export interface EditableDateProps {
  label: string;
  value?: string;
  type?: 'date' | 'datetime-local';
  displayValue: string;
  onSave?: (dateValue: string) => void;
}

/** Editable date field — click to open a popover with a date input. */
export const EditableDate: React.FC<EditableDateProps> = ({
  label,
  value,
  type = 'date',
  displayValue,
  onSave,
}) => {
  const anchorRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [draftDate, setDraftDate] = useState<dayjs.Dayjs | null>(null);
  const [draftTime, setDraftTime] = useState<dayjs.Dayjs | null>(null);
  const [validationError, setValidationError] = useState('');

  const handleOpen = () => {
    const d = value ? dayjs(value) : null;
    setDraftDate(d?.isValid() ? d : null);
    setDraftTime(d?.isValid() ? d : dayjs().hour(9).minute(0));
    setValidationError('');
    setOpen(true);
  };

  const handleSave = () => {
    if (!onSave) return;
    if (!draftDate || !draftDate.isValid()) {
      setValidationError('Please enter a valid date');
      return;
    }

    let result = draftDate;
    if (type === 'datetime-local') {
      if (!draftTime || !draftTime.isValid()) {
        setValidationError('Please enter a valid time');
        return;
      }
      result = draftDate.hour(draftTime.hour()).minute(draftTime.minute()).second(0);
    }

    setValidationError('');
    setOpen(false);
    onSave(formatLocalDateTime(result));
  };

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            fontSize: '9px',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            lineHeight: 1.2,
            mb: 0.25,
          }}
        >
          {label}
        </Typography>
      )}
      <Box ref={anchorRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box component="span" sx={{ fontSize: '13px', lineHeight: 1.3 }}>
          {displayValue}
        </Box>
        {onSave && (
          <IconButton
            size="small"
            onClick={handleOpen}
            aria-label={`Edit ${label || 'date'}`}
            sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'v4.accent' } }}
          >
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: POPOVER_Z_INDEX }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={8}
            sx={{
              p: 2,
              mt: 0.5,
              border: '1px solid',
              borderColor: 'v4.border',
              borderRadius: 2,
              width: 280,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-au">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <DatePicker
                  label="Date"
                  value={draftDate}
                  onChange={(val) => {
                    setDraftDate(val);
                    setValidationError('');
                  }}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      size: 'small',
                      autoFocus: true,
                      sx: { '& .MuiOutlinedInput-root': { fontSize: '12px' } },
                    },
                    // The calendar is portalled onto MUI's modal layer, below
                    // this popover — lift it so it isn't painted behind.
                    popper: { sx: { zIndex: OVERLAY_Z_INDEX } },
                  }}
                />
                {type === 'datetime-local' && (
                  <TimePicker
                    label="Time"
                    value={draftTime}
                    onChange={(val) => {
                      setDraftTime(val);
                      setValidationError('');
                    }}
                    ampm={true}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { '& .MuiOutlinedInput-root': { fontSize: '12px' } },
                      },
                      popper: { sx: { zIndex: OVERLAY_Z_INDEX } },
                    }}
                  />
                )}
                {validationError && (
                  <Typography sx={{ fontSize: '11px', color: 'error.main' }}>
                    {validationError}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setOpen(false)}
                    sx={{
                      fontSize: '11px',
                      px: 1.5,
                      py: 0.5,
                      border: 'none',
                      bgcolor: 'transparent',
                      cursor: 'pointer',
                      color: 'text.secondary',
                      fontFamily: 'inherit',
                    }}
                  >
                    Cancel
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    onClick={handleSave}
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      px: 1.5,
                      py: 0.5,
                      border: 'none',
                      borderRadius: '4px',
                      bgcolor: 'v4.accent',
                      color: 'common.white',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      '&:hover': { opacity: 0.9 },
                    }}
                  >
                    Save
                  </Box>
                </Box>
              </Box>
            </LocalizationProvider>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
